// Shared pricing data — single source of truth for tier numbers across
// the repo. The billing-service uses these to compute rates and grants
// at apply time; the dashboard's /pricing page renders them; the
// gateway's tier-derived rate-limit caps live independently in
// gateway/src/billing/tiers.ts (those caps don't change with the peg
// and the gateway shouldn't pull cross-repo just to cap RPS).
//
// Editing this file is the only place to change tier prices, grants,
// or RPS/sub caps. PRICING.md mirrors the table for human reading;
// keep both in sync when committing peg changes (one PR per change).

// ============================================================
// Types
// ============================================================

export type TierName        = 'trial' | 'hobby' | 'build' | 'scale' | 'business' | 'dedicated';
export type SubscriptionTerm = 'monthly' | 'annual';

export interface TierDefinition {
  /** Wire identifier — matches the value stored in `accounts.tier`. */
  name: TierName;
  /** Trial is one-time / time-boxed; everything else is a recurring monthly bundle. */
  kind: 'one_time' | 'recurring';
  /** Monthly USD price. 0 for trial; placeholder floor for Dedicated (real value negotiated). */
  monthly_price_usd: number;
  /** Monthly CC bundle. Annual subscribers get 12× this granted at cycle start. */
  cc_quota_monthly: number;
  /** Steady-state RPS cap. Token bucket refills at this rate per second. */
  rps_cap: number;
  /** Burst capacity — bucket size in tokens. Lets a client spike well above
   *  rps_cap for a short window, then refill at rps_cap/sec. Sized 20× rps_cap
   *  so a typical wallet cold-start (Cashonize fans out 200–700 RPS for <1s
   *  to download tx history) fits comfortably in the bucket without any 429s,
   *  while sustained load is still capped at rps_cap. */
  burst_cap: number;
  /** Max concurrent WebSocket subscriptions across an account's tokens. */
  max_concurrent_subs: number;
  /** Max active tokens an account can have at once. */
  max_tokens: number;
  /** Trial-only: one-time validity window. Other tiers get 30/365-day cycles. */
  trial_duration_days?: number;
  /** Per-tier override of DEFAULT_ANNUAL_DISCOUNT_PCT. Fraction in [0,1]. */
  annual_discount_pct?: number;
}

// ============================================================
// Defaults
// ============================================================

/**
 * Default annual discount: 1/6 (≈16.67%) → 12 × (5/6) = 10. The "two
 * months free" framing customers see on the pricing page.
 */
export const DEFAULT_ANNUAL_DISCOUNT_PCT = 1 / 6;

// ============================================================
// The table
// ============================================================
//
// Numbers here mirror PRICING.md §3 1:1. Per-CC rate ladder:
// Hobby $0.0333 > Build $0.0308 > Business $0.0300 > Scale $0.0211
// (monotonic descending — bigger tier → cheaper per CC). Build was
// bumped 800M → 1.3B to clear the previous Hobby > Build inversion.

export const TIERS: Readonly<Record<TierName, TierDefinition>> = Object.freeze({
  trial: {
    name:                'trial',
    kind:                'one_time',
    monthly_price_usd:   0,
    cc_quota_monthly:    5_000_000,           // 5M CC, hard-stops; no overage
    rps_cap:             5,
    burst_cap:           100,                 // 20× rps_cap
    max_concurrent_subs: 5,
    max_tokens:          1,
    trial_duration_days: 14,
  },
  hobby: {
    name:                'hobby',
    kind:                'recurring',
    monthly_price_usd:   9.99,
    cc_quota_monthly:    300_000_000,         // 300M CC ($0.0333/M; entry/loss-leader rate)
    rps_cap:             25,
    burst_cap:           500,
    max_concurrent_subs: 25,
    max_tokens:          3,
  },
  build: {
    name:                'build',
    kind:                'recurring',
    monthly_price_usd:   39.99,
    cc_quota_monthly:    1_300_000_000,       // 1.3B CC ($0.0308/M; sits between Hobby and Business)
    rps_cap:             75,
    burst_cap:           1_500,
    max_concurrent_subs: 100,
    max_tokens:          10,
  },
  scale: {
    name:                'scale',
    kind:                'recurring',
    monthly_price_usd:   199.99,
    cc_quota_monthly:    9_500_000_000,       // 9.5B CC ($0.0211/M; volume sweet spot)
    rps_cap:             200,
    burst_cap:           4_000,
    max_concurrent_subs: 500,
    max_tokens:          25,
  },
  business: {
    name:                'business',
    kind:                'recurring',
    monthly_price_usd:   599.99,
    cc_quota_monthly:    20_000_000_000,      // 20B CC ($0.0300/M; pays for features more than credits)
    rps_cap:             500,
    burst_cap:           10_000,
    max_concurrent_subs: 2500,
    max_tokens:          50,
  },
  dedicated: {
    name:                'dedicated',
    kind:                'recurring',
    // Placeholder — real Dedicated pricing is negotiated. PRICING.md §6
    // sets the floor at $1500/mo and the top-up rate at $0.0200/M CC
    // (matches Scale's published rate so negotiation can't race below
    // the volume tier).
    monthly_price_usd:   1500,
    cc_quota_monthly:    50_000_000_000,
    rps_cap:             2500,
    burst_cap:           50_000,
    max_concurrent_subs: 10_000,
    max_tokens:          200,
  },
});

// ============================================================
// Top-up policy
// ============================================================

/** Top-up minimum (USD) — see BILLING.md §7. */
export const TOPUP_MIN_USD = 5;

/** Headline marketing peg — Scale's effective $/M CC. */
export const HEADLINE_USD_PER_M_CC = 0.02;

// ============================================================
// Pure helpers — same shape both sides expect
// ============================================================

export function annualDiscountFor(tier: TierName): number {
  const def = TIERS[tier];
  if (def.kind === 'one_time') return 0;
  return def.annual_discount_pct ?? DEFAULT_ANNUAL_DISCOUNT_PCT;
}

export function bundlePriceUsd(tier: TierName, term: SubscriptionTerm): number {
  const def = TIERS[tier];
  if (term === 'monthly') return def.monthly_price_usd;
  return def.monthly_price_usd * 12 * (1 - annualDiscountFor(tier));
}

export function bundleGrantCc(tier: TierName, term: SubscriptionTerm): number {
  const def = TIERS[tier];
  if (term === 'monthly') return def.cc_quota_monthly;
  return def.cc_quota_monthly * 12;
}

export function bundleRatePerCc(tier: TierName, term: SubscriptionTerm): number {
  if (TIERS[tier].kind === 'one_time') return 0;
  const grant = bundleGrantCc(tier, term);
  if (grant === 0) return 0;
  return bundlePriceUsd(tier, term) / grant;
}

export function cycleDays(term: SubscriptionTerm): number {
  return term === 'annual' ? 365 : 30;
}

export function cycleMs(term: SubscriptionTerm): number {
  return cycleDays(term) * 24 * 60 * 60 * 1000;
}
